import { ResultDisplayProps } from '../../common'
import React, { useMemo } from 'react'
import { AIResultStatus } from '@zocks-communications/client-sdk-js'
import { NoMeetingResult } from '../../DefaultDisplay/View/NoMeetingResult'
import { StructuredSummaryResult } from '../../../../../../../models/AiResult'
import './FutureMeetings.scss'
import { Button } from '../../../../../../../components/Button/Button'
import { formatDate } from '../../../../common/aifeatures/utils'
import { useGetMeQuery } from '../../../../../../../store/user/user'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { selectRoomSession } from '../../../../../../../store/meetingSummary/roomSession'
import { IntegrationServiceId } from '../../../../../../../common/integrations'
import { CalendarProvider, type FutureMeetingProps, generateCalendarDeepLink, normalizeFutureMeetings } from './utils'

const ONE_HOUR_IN_MS = 60 * 60 * 1000

export const FutureMeetings: React.FC<ResultDisplayProps> = ({ aiResult }) => {
  if (aiResult.status === AIResultStatus.NO_RESULT) {
    return <NoMeetingResult />
  }
  const structuredSummaryResult = aiResult.result as StructuredSummaryResult
  const meetings = normalizeFutureMeetings(structuredSummaryResult.result)

  return (
    <div>
      <div className='d-flex flex-column gap-3'>
        {meetings.map(({ name, date }) => (
          <FutureMeeting key={`${name}-${date}`} name={name} date={date} />
        ))}
      </div>
    </div>
  )
}

const FutureMeeting: React.FC<FutureMeetingProps> = ({ name, date }) => {
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)

  const { data: currentUser } = useGetMeQuery({ accountId })
  const hasGoogleCalendarIntegration = useMemo(() => {
    return !!(currentUser && currentUser.integrations && currentUser.integrations.some((i) => i.serviceId === IntegrationServiceId.GOOGLE_CALENDAR))
  }, [currentUser])
  const hasOutlookCalendarIntegration = useMemo(() => {
    return !!(currentUser && currentUser.integrations && currentUser.integrations.some((i) => i.serviceId === IntegrationServiceId.OUTLOOK_CALENDAR))
  }, [currentUser])
  const hasCalendarIntegration = useMemo(
    () => hasGoogleCalendarIntegration || hasOutlookCalendarIntegration,
    [hasGoogleCalendarIntegration, hasOutlookCalendarIntegration],
  )

  const validDate = date !== null && !isNaN(Date.parse(date)) ? new Date(date) : null
  const eventDate = validDate ?? new Date()
  const formattedDate = validDate !== null ? formatDate(validDate.toISOString()) : 'Date not mentioned.'

  const handleClickOnAddEvent = () => {
    if (hasCalendarIntegration && roomSession) {
      const endDate = new Date(eventDate.getTime() + ONE_HOUR_IN_MS)
      const emails = roomSession.users.filter((u) => u.email != null).map((u) => u.email)
      const provider = hasGoogleCalendarIntegration ? CalendarProvider.GOOGLE : CalendarProvider.OUTLOOK
      const url = generateCalendarDeepLink(provider, name, eventDate, endDate, emails)
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div className='future-meeting-container p-3'>
      <div className='d-flex justify-content-between'>
        <div>
          <div className='paragraph-large bold'>{name}</div>
          <div>{formattedDate}</div>
        </div>
        <div className='d-flex align-items-center'>
          <OverlayTrigger trigger={!hasCalendarIntegration ? ['hover', 'focus'] : []} overlay={<Tooltip>Connect your calendar.</Tooltip>}>
            <div>
              <Button variant='primary' className=' d-flex align-items-center' onClick={handleClickOnAddEvent} disabled={!hasCalendarIntegration}>
                +
              </Button>
            </div>
          </OverlayTrigger>
        </div>
      </div>
    </div>
  )
}
